import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import { updateFiscalSession } from 'src/app/redux/actions/user.actions';
import { AppState } from 'src/app/redux/app.state';
import { FiscalTypeEnum } from 'src/enums';
import { Fiscal, User } from 'src/typings';
import { FiscalService } from '../fiscal.service';
import { FiscalSettingsDialogComponent } from '../settings-dialog/settings-dialog.component';
import { XReportComponent } from '../x-report/x-report.component';

type SessionFilter = 'all' | 'open' | 'closed';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements AfterViewInit, OnDestroy {
  readonly displayedColumns = ['id', 'cashbox', 'seller', 'mode', 'session', 'sum', 'action'];

  readonly typeOptions = [
    { value: 'all', label: 'Все типы' },
    { value: FiscalTypeEnum.fiscal, label: 'Фискальная' },
    { value: FiscalTypeEnum.test, label: 'Тестовая' }
  ];

  readonly sessionOptions: Array<{ value: SessionFilter; label: string }> = [
    { value: 'all', label: 'Все сессии' },
    { value: 'open', label: 'Сессия открыта' },
    { value: 'closed', label: 'Сессия закрыта' }
  ];

  readonly searchForm: UntypedFormGroup = this.fb.group({
    query: new UntypedFormControl(''),
    type: new UntypedFormControl('all'),
    session: new UntypedFormControl('all')
  });

  readonly dataSource = new MatTableDataSource<Fiscal>([]);

  allFiscals: Fiscal[] = [];
  loading = false;
  user: User | undefined;

  private readonly subscriptions = new Subscription();

  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly fiscalService: FiscalService,
    private readonly notificationService: NotificationService,
    private readonly store: Store<AppState>,
    private readonly dialog: MatDialog,
    public readonly supportService: SupportService
  ) {
    this.dataSource.filterPredicate = (fiscal, rawFilter) => {
      const filter = JSON.parse(rawFilter) as {
        query: string;
        type: 'all' | FiscalTypeEnum;
        session: SessionFilter;
      };

      if (filter.type !== 'all' && fiscal.type !== filter.type) {
        return false;
      }

      if (filter.session === 'open' && !fiscal.isSessionOpen) {
        return false;
      }

      if (filter.session === 'closed' && fiscal.isSessionOpen) {
        return false;
      }

      const query = filter.query.trim().toUpperCase();
      if (!query) {
        return true;
      }

      const searchable = [
        fiscal.id,
        fiscal.name,
        fiscal.address,
        fiscal.sellerDisplayName,
        fiscal.unitDisplayName,
        fiscal.displayTaxIdLabel,
        fiscal.displayTaxIdValue,
        fiscal.fullAddress,
        fiscal.prroFiscalNumber
      ]
        .filter((value) => value !== null && typeof value !== 'undefined')
        .join(' ')
        .toUpperCase();

      return searchable.includes(query);
    };

    this.subscriptions.add(
      this.fiscalService.sessionChanges.subscribe(() => {
        this.loadFiscals(false);
      })
    );

    this.subscriptions.add(
      this.store.subscribe((storeItems) => {
        this.user = storeItems.user.info;
      })
    );

    this.subscriptions.add(
      this.searchForm.valueChanges.subscribe(() => {
        this.applyFilters();
      })
    );

    this.loadFiscals();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator || null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadFiscals(resetPaging = true): void {
    this.loading = true;
    this.fiscalService
      .search({
        limit: 200,
        offset: 0
      })
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.allFiscals = response.data;
          this.dataSource.data = response.data;
          if (resetPaging) {
            this.paginator?.firstPage();
          }
          this.applyFilters();
        },
        error: (error) => {
          console.error(error);
          this.loading = false;
          this.notificationService.showError('Не удалось загрузить список касс');
        }
      });
  }

  applyFilters(): void {
    this.dataSource.filter = JSON.stringify({
      query: this.searchForm.value.query || '',
      type: this.searchForm.value.type || 'all',
      session: this.searchForm.value.session || 'all'
    });

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clearFilters(): void {
    this.searchForm.reset({
      query: '',
      type: 'all',
      session: 'all'
    });
  }

  openCreateDialog(): void {
    this.openSettingsDialog();
  }

  editFiscal(fiscal: Fiscal, event?: Event): void {
    event?.stopPropagation();
    this.openSettingsDialog(fiscal);
  }

  private openSettingsDialog(fiscal?: Fiscal): void {
    const dialogRef = this.dialog.open(FiscalSettingsDialogComponent, {
      data: { fiscal: fiscal || null }
    });

    this.subscriptions.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadFiscals(false);
        }
      })
    );
  }

  deleteFiscal(fiscal: Fiscal, event?: Event): void {
    event?.stopPropagation();
    const confirmed = window.confirm(`Видалити касу "${fiscal.name}"?`);
    if (!confirmed) {
      return;
    }

    this.loading = true;
    this.fiscalService.delete(fiscal.id).subscribe({
      next: () => {
        this.loading = false;
        this.notificationService.notice('Касса удалена');
        this.loadFiscals(false);
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.showError('Не удалось удалить кассу');
      }
    });
  }

  openSession(fiscal: Fiscal, event?: Event): void {
    event?.stopPropagation();
    const userId = this.user?.id;
    this.loading = true;
    this.fiscalService.openSession(fiscal.id).subscribe({
      next: () => {
        if (userId) {
          this.store.dispatch(updateFiscalSession({ userId, fiscalId: fiscal.id, isSessionOpen: true }));
        }
        this.loading = false;
        this.notificationService.notice(`Сессия открыта: ${fiscal.name}`);
        this.loadFiscals(false);
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.showError('Не удалось открыть сессию');
      }
    });
  }

  closeSession(fiscal: Fiscal, event?: Event): void {
    event?.stopPropagation();
    const userId = this.user?.id;
    this.loading = true;
    this.fiscalService.closeSession(fiscal.id).subscribe({
      next: () => {
        if (userId) {
          this.store.dispatch(updateFiscalSession({ userId, fiscalId: fiscal.id, isSessionOpen: false }));
        }
        this.loading = false;
        this.notificationService.notice(`Сессия закрыта: ${fiscal.name}`);
        this.loadFiscals(false);
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.showError('Не удалось закрыть сессию');
      }
    });
  }

  xReport(id: number, event?: Event): void {
    event?.stopPropagation();
    this.loading = true;
    this.fiscalService.xReport(id).subscribe({
      next: (data) => {
        this.loading = false;
        const dialogRef = this.dialog.open(XReportComponent);
        dialogRef.componentInstance.date = new Date(data.info.dt);
        dialogRef.componentInstance.sum = data.info.safe;
        dialogRef.componentInstance.header = data.info.printheader.name;
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.showError('Не удалось получить X-отчет');
      }
    });
  }

  collection(id: number, event?: Event): void {
    event?.stopPropagation();
    this.loading = true;
    this.fiscalService.xReport(id).subscribe({
      next: (data) => {
        const sum = data.info.safe;
        this.fiscalService.collect({ id, sum }).subscribe({
          next: () => {
            this.fiscalService.addMoney({ id, sum: 500 }).subscribe({
              next: () => {
                this.loading = false;
                this.notificationService.notice(
                  `Касса №${id} инкассирована. Остаток ${this.supportService.formatMoney(500)}`
                );
                this.loadFiscals(false);
              },
              error: (error) => {
                console.error(error);
                this.loading = false;
                this.notificationService.showError('Не удалось выполнить внесение после инкассации');
              }
            });
          },
          error: (error) => {
            console.error(error);
            this.loading = false;
            this.notificationService.showError('Не удалось выполнить инкассацию');
          }
        });
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
        this.notificationService.showError('Не удалось получить X-отчет для инкассации');
      }
    });
  }

  getTypeLabel(fiscal: Fiscal): string {
    if (fiscal.type === FiscalTypeEnum.fiscal) {
      return 'Фискальная';
    }
    return 'Тестовая';
  }

  getSessionLabel(fiscal: Fiscal): string {
    return fiscal.isSessionOpen ? 'Сессия открыта' : 'Сессия закрыта';
  }

  isCurrentFiscal(fiscal: Fiscal): boolean {
    return Number(this.user?.fiscal?.id) === Number(fiscal.id);
  }

  getSellerIdentity(fiscal: Fiscal): string {
    if (fiscal.displayTaxIdLabel && fiscal.displayTaxIdValue) {
      return `${fiscal.displayTaxIdLabel}: ${fiscal.displayTaxIdValue}`;
    }
    return fiscal.prroFiscalNumber ? `ФН ПРРО: ${fiscal.prroFiscalNumber}` : 'Реквизиты не заполнены';
  }
}
